import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Zap, Euro, Sunrise, Mail, X, Sparkles,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Notification {
  id: string;
  type: "chain" | "invoice" | "briefing" | "email";
  icon: string;
  title: string;
  message: string;
  link: string;
  timestamp: string;
  time_ago: string;
  severity: "success" | "info" | "warning" | "highlight";
}

const ICON_MAP: Record<string, React.ElementType> = {
  zap: Zap,
  euro: Euro,
  sunrise: Sunrise,
  mail: Mail,
};

const SEVERITY_STYLE: Record<string, { dot: string; bg: string; border: string }> = {
  success: { dot: "bg-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  info: { dot: "bg-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  warning: { dot: "bg-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  highlight: { dot: "bg-primary", bg: "bg-primary/10", border: "border-primary/30" },
};

const LS_SEEN_KEY = "grafton-notifs-seen";

function loadSeenIds(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(LS_SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveSeenIds(set: Set<string>) {
  localStorage.setItem(LS_SEEN_KEY, JSON.stringify([...set]));
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<Set<string>>(loadSeenIds());
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/bell`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setNotifications(d.notifications || []);
      }
    } catch {/* empty */}
  };

  useEffect(() => {
    load();
    // Poll every 60s
    pollRef.current = setInterval(load, 60000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const unseenCount = notifications.filter((n) => !seen.has(n.id)).length;

  const handleClick = async (notif: Notification) => {
    const next = new Set(seen);
    next.add(notif.id);
    setSeen(next);
    saveSeenIds(next);

    if (notif.type === "briefing") {
      try {
        await fetch(`${API_URL}/api/notifications/mark-briefing-viewed`, {
          method: "POST",
          credentials: "include",
        });
      } catch {/* empty */}
    }

    setOpen(false);
    navigate(notif.link);
  };

  const markAllSeen = () => {
    const next = new Set(seen);
    notifications.forEach((n) => next.add(n.id));
    setSeen(next);
    saveSeenIds(next);
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative h-9 w-9 rounded-lg border border-border/50 bg-card/50 hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center"
        data-testid="notification-bell-trigger"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4 text-muted-foreground" />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background" data-testid="notification-badge">
            {unseenCount > 9 ? "9+" : unseenCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-[380px] rounded-xl border border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl overflow-hidden" data-testid="notification-dropdown">
            {/* Header */}
            <div className="px-4 py-3 border-b border-border/30 flex items-center justify-between bg-gradient-to-r from-primary/10 via-transparent to-transparent">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold">Notifications</h3>
                {unseenCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-semibold">{unseenCount} nouvelles</span>}
              </div>
              <div className="flex gap-1">
                {notifications.length > 0 && (
                  <button
                    onClick={markAllSeen}
                    className="text-[10px] text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded"
                    data-testid="mark-all-seen"
                  >
                    Tout marquer lu
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="h-6 w-6 rounded hover:bg-muted/50 flex items-center justify-center">
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="max-h-[440px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-10 text-center">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">Tout est à jour !</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Grafton surveille vos flux 24/7.</p>
                </div>
              ) : (
                <div className="divide-y divide-border/20">
                  {notifications.map((n) => {
                    const Icon = ICON_MAP[n.icon] || Bell;
                    const style = SEVERITY_STYLE[n.severity] || SEVERITY_STYLE.info;
                    const isUnseen = !seen.has(n.id);
                    return (
                      <button
                        key={n.id}
                        onClick={() => handleClick(n)}
                        className={`w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-muted/20 transition-colors ${isUnseen ? "bg-primary/5" : ""}`}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <div className={`h-9 w-9 rounded-lg ${style.bg} border ${style.border} flex items-center justify-center shrink-0`}>
                          <Icon className="h-4 w-4 text-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold truncate">{n.title}</p>
                            {isUnseen && <span className={`h-1.5 w-1.5 rounded-full ${style.dot} shrink-0`} />}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                          <p className="text-[10px] text-muted-foreground/70 mt-1">{n.time_ago}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-border/30 bg-muted/10">
              <p className="text-[10px] text-center text-muted-foreground">
                Grafton rafraîchit toutes les minutes
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
